/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { TableContent } from '../Table/Table.defs';
import { ApiTableFetchOptions, ApiTablePageTokens } from './ApiTable.defs';

/**
 * The props for `<ApiTable />`.
 */
export interface ApiTableProps {
  /**
   * Key that triggers a table refresh when changed.
   */
  cacheKey?: string;

  /**
   * Additional classes to apply to the table.
   */
  classes?: string;

  /**
   * Text to display for empty table content.
   */
  emptyText?: string;

  /**
   * Callback to fetch new data for the table.
   */
  fetchData: (options: ApiTableFetchOptions) => void;

  /**
   * Set the initial fetch state.
   */
  initialFetch?: boolean;

  /**
   * Toggle slim appearance for the table.
   */
  isSlim?: boolean;

  /**
   * Table loading state.
   */
  loading: boolean;

  /**
   * Text to display when no more items are available.
   */
  noMoreText?: string;

  /**
   * Contains next/previous data for token based pagination.
   */
  pageTokens?: ApiTablePageTokens;

  /**
   * Specify the default rows per page selection.
   */
  perPageDefault?: number;

  /**
   * Specify the rows per page select field options.
   */
  perPageOptions?: Array<number>;

  /**
   * Text to accompany the rows per page select field.
   */
  perPageText?: string;

  /**
   * Table content.
   */
  tableContent: TableContent;

  /**
   * Defines if table items are selectable.
   */
  isSelectable?: boolean;

  /**
   * Defines if the table is paginated.
   */
  isPaginated?: boolean;

  /**
   * Callback to execute on row state change. This is used to manage row selection and to hoist the state off the component. This is required if `isSelectable` is true.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowSelect?: (rows: Array<any>) => void;

  /**
   * Callback to execute on row double click.
   */
  onRowDoubleClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
}

/**
 * The default prop values for `<ApiTable />`.
 */
export const apiTableDefaults = {
  emptyText: `No entries found.`,
  isSlim: false,
  loading: true,
  noMoreText: `No more entries found`,
  perPageDefault: 10,
  perPageOptions: [10, 20, 50, 100],
  perPageText: `Rows per page:`,
};
